import { PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { App, Button, Empty, Input, Popconfirm, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useMemo, useState } from 'react';
import { getApiError } from '../../api/client';
import { formatNumber, MoneyText } from '../../lib/format';
import { useIsMobile } from '../../lib/responsive';
import { usePersistedState } from '../../lib/usePersistedState';
import type { Product } from '../../api/types';
import { useFirmaCurenta } from '../firme/FirmaCurentaContext';
import { deleteProdus, fetchProduse, fetchVatRates } from './api';
import { ProdusFormDrawer } from './ProdusFormDrawer';

const { Title } = Typography;
const PAGE_SIZE = 20;
const VAT_RATES_STALE_TIME = 60 * 60 * 1000;

export function ProduseListPage() {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const { firmaId } = useFirmaCurenta();
  const isMobile = useIsMobile();
  const [page, setPage] = usePersistedState('produse.page', 1);
  const [search, setSearch] = usePersistedState('produse.search', '');
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['products', firmaId, { page, pageSize: PAGE_SIZE, search }],
    queryFn: () => fetchProduse({ companyId: firmaId as string, page, pageSize: PAGE_SIZE, search: search || undefined }),
    enabled: !!firmaId,
  });

  const { data: vatRates } = useQuery({
    queryKey: ['vat-rates'],
    queryFn: fetchVatRates,
    staleTime: VAT_RATES_STALE_TIME,
  });

  const rateByCategory = useMemo(() => {
    const map = new Map<string, string>();
    for (const rate of vatRates ?? []) map.set(rate.category, rate.ratePercent);
    return map;
  }, [vatRates]);

  const deleteMutation = useMutation({
    mutationFn: deleteProdus,
    onSuccess: () => {
      message.success('Produsul a fost șters.');
      queryClient.invalidateQueries({ queryKey: ['products', firmaId] });
    },
    onError: (error) => {
      const apiError = getApiError(error);
      message.error(apiError?.message ?? 'Produsul nu a putut fi șters.');
    },
  });

  function openCreate() {
    setEditingId(null);
    setDrawerOpen(true);
  }
  function openEdit(id: string) {
    setEditingId(id);
    setDrawerOpen(true);
  }
  function closeDrawer() {
    setDrawerOpen(false);
    setEditingId(null);
  }

  const columns: ColumnsType<Product> = [
    { title: 'Denumire', dataIndex: 'name' },
    { title: 'UM', dataIndex: 'unitLabel' },
    {
      title: 'Preț unitar',
      dataIndex: 'unitPrice',
      align: 'right',
      render: (v: string, row) => <MoneyText value={v} currency={row.currency} />,
    },
    {
      title: 'Cotă TVA',
      dataIndex: 'vatCategory',
      render: (v: string) => {
        const rate = rateByCategory.get(v);
        return <Tag>{rate ? `${formatNumber(rate)}%` : '—'}</Tag>;
      },
    },
    { title: 'Monedă', dataIndex: 'currency' },
    {
      title: 'Stare',
      dataIndex: 'isActive',
      render: (v: boolean) => (v ? <Tag color="green">Activ</Tag> : <Tag>Inactiv</Tag>),
    },
    {
      title: 'Acțiuni',
      key: 'actions',
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row.id)}>
            Editează
          </Button>
          <Popconfirm
            title="Ștergi produsul?"
            description="Produsul poate fi șters doar dacă nu este folosit pe facturi sau abonamente."
            onConfirm={() => deleteMutation.mutate(row.id)}
          >
            <Button size="small" danger loading={deleteMutation.isPending && deleteMutation.variables === row.id}>
              Șterge
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Title level={3} style={{ margin: '0 0 16px' }}>
        Produse
      </Title>
      {!firmaId ? (
        <Empty description="Selectează sau adaugă mai întâi o firmă." />
      ) : (
        <>
          <div
            style={{
              marginBottom: 16,
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              justifyContent: 'space-between',
              gap: 8,
            }}
          >
            <Input.Search
              placeholder="Caută după denumire"
              allowClear
              defaultValue={search}
              style={{ width: isMobile ? '100%' : 320 }}
              onSearch={(value) => {
                setSearch(value);
                setPage(1);
              }}
            />
            <Button type="primary" icon={<PlusOutlined />} block={isMobile} onClick={openCreate}>
              Adaugă produs
            </Button>
          </div>
          <Table<Product>
            rowKey="id"
            columns={columns}
            dataSource={data?.items}
            loading={isLoading}
            scroll={{ x: 'max-content' }}
            pagination={{
              current: page,
              pageSize: PAGE_SIZE,
              total: data?.total ?? 0,
              onChange: setPage,
              showSizeChanger: false,
            }}
          />
          <ProdusFormDrawer
            open={drawerOpen}
            firmaId={firmaId}
            editingId={editingId}
            vatRates={vatRates ?? []}
            onClose={closeDrawer}
          />
        </>
      )}
    </div>
  );
}
